<form>
  <div class="d-flex w-100 align-items-start">
    <!-- Left pane: the tour at a glance — metadata row + ordered step list. -->
    <div class="me-3" style="width: 285px; flex: none">
      <ul class="list-unstyled m-0">
        <li
          class="d-flex align-items-center p-2 border-bottom"
          style="cursor: pointer"
          [class.k-state-selected]="selected === metadataRow"
          (click)="select(metadataRow)"
        >
          <strong class="text-truncate">
            {{ document.title || '(untitled tour)' }}
          </strong>
          <small class="text-muted ms-auto">{{ steps.length }} steps</small>
        </li>
        @for (step of steps; track step; let i = $index) {
          <li
            class="d-flex align-items-center p-2 border-bottom"
            style="cursor: pointer"
            [class.k-state-selected]="selected === i"
            (click)="select(i)"
          >
            <span class="text-muted me-2">{{ i + 1 }}</span>
            <small class="text-muted text-uppercase me-2">
              {{ isNavStep(step) ? 'Nav' : 'El' }}
            </small>
            <span class="text-truncate">
              {{ step.title || step.path || '(empty)' }}
            </span>
            <span class="ms-auto d-flex align-items-center">
              @if (!step.path?.trim()) {
                <span class="k-icon k-i-warning text-danger me-1" title="No element or route set"></span>
              } @else if (isElementStep(step) && !resolvesHere(step)) {
                <small class="text-muted me-1" title="Not on this page (fine for cross-page tours)">off-page</small>
              }
              <button
                type="button"
                class="k-button k-button-sm"
                title="Move up"
                [disabled]="i === 0"
                (click)="move(i, -1); $event.stopPropagation()"
              >
                <span class="k-icon k-i-arrow-60-up"></span>
              </button>
              <button
                type="button"
                class="k-button k-button-sm"
                title="Move down"
                [disabled]="i === steps.length - 1"
                (click)="move(i, 1); $event.stopPropagation()"
              >
                <span class="k-icon k-i-arrow-60-down"></span>
              </button>
              <button
                type="button"
                class="k-button k-button-sm"
                title="Remove step"
                (click)="removeStep(i); $event.stopPropagation()"
              >
                <span class="k-icon k-i-close"></span>
              </button>
            </span>
          </li>
        }
      </ul>
      <div class="d-flex mt-2">
        <button type="button" class="k-button k-button-sm" (click)="addStep('element')">
          + Element Step
        </button>
        <button type="button" class="k-button k-button-sm ms-1" (click)="addStep('nav')">
          + Navigation Step
        </button>
      </div>
    </div>

    <!-- Right pane: edit the selected row. -->
    <div class="flex-grow-1">
      @if (selected === metadataRow) {
        <fieldset>
          <legend>Walkthrough Metadata</legend>
          <label class="d-block k-label">
            <input
              type="checkbox"
              kendoCheckBox
              name="pageSpecificToggle"
              #checkbox
              [checked]="!!f.path().value()"
              (change)="updatePathBasedOnCheckbox(checkbox.checked)"
            />
            Is Page Specific?
          </label>
          @if (!checkbox.checked) {
            <label class="d-block k-label">
              Anchor Element (right click to select)
              <help-element-picker class="w-100" help-anchor="elementControl" [formField]="f.path"></help-element-picker>
            </label>
          }
          <label class="d-block k-label">
            Title
            <input class="w-100 k-textbox" help-anchor="titleControl" [formField]="f.title" />
          </label>
          <label class="d-block k-label">
            Description
            <kendo-editor help-anchor="bodyControl" [value]="model().html" (valueChange)="setHtml($event)"></kendo-editor>
          </label>
        </fieldset>
      } @else {
        @if (steps[selected]; as step) {
        <fieldset>
          <legend class="d-flex align-items-center">
            <span>Step {{ selected + 1 }} / {{ steps.length }}</span>
            <span class="ms-auto">
              <kendo-buttongroup [selection]="'single'">
                <button
                  kendoButton
                  type="button"
                  [togglable]="true"
                  [selected]="isElementStep(step)"
                  (click)="setKind(step, 'element')"
                >
                  Element
                </button>
                <button
                  kendoButton
                  type="button"
                  [togglable]="true"
                  [selected]="isNavStep(step)"
                  (click)="setKind(step, 'nav')"
                >
                  Navigation
                </button>
              </kendo-buttongroup>
            </span>
          </legend>

          @if (isNavStep(step)) {
            <label class="d-block k-label">
              Route (the page this step navigates to)
              <span class="d-flex align-items-center">
                <input
                  class="w-100 k-textbox"
                  help-anchor="navRouteControl"
                  placeholder="/time-entry" [value]="f.path().value() ?? ''" (input)="onNavPathInput(step, $event)"
                />
                <button
                  type="button"
                  class="k-button k-button-sm ms-2"
                  (click)="useCurrentPage(step)"
                >
                  Use current page
                </button>
              </span>
            </label>
            <small class="text-muted d-block">
              The player spotlights the menu path to this route and moves the
              user there before the next step.
            </small>
          } @else {
            <label class="d-block k-label">
              Element (right click to select)
              <span class="d-flex align-items-center">
                <help-element-picker class="w-100" help-anchor="elementControl" [formField]="f.path"></help-element-picker>
                <button
                  type="button"
                  class="k-button k-button-sm ms-2"
                  title="Fly the wren to this element"
                  [disabled]="!step.path?.trim()"
                  (click)="testFly(step, selected)"
                >
                  Test
                </button>
              </span>
            </label>
            @if (lastTest?.index === selected) {
              <small
                class="d-block"
                [class.text-muted]="lastTest.found"
                [class.text-danger]="!lastTest.found"
              >
                {{
                  lastTest.found
                    ? 'Found it — the wren is pointing at the element.'
                    : 'Not on this page. For a cross-page tour, put a navigation step before this one.'
                }}
              </small>
            }
          }

          <label class="d-block k-label">
            Title
            <input class="w-100 k-textbox" help-anchor="titleControl" [formField]="f.title" />
          </label>
          <label class="d-block k-label">
            Content
            <kendo-editor help-anchor="bodyControl" [value]="model().html" (valueChange)="setHtml($event)"></kendo-editor>
          </label>
        </fieldset>
        }
      }
    </div>
  </div>

  <div class="d-flex align-items-center mt-2">
    @if (invalidSteps.length) {
      <small class="text-danger">
        {{ invalidSteps.length }} step(s) have no element or route set.
      </small>
    } @else if (unresolvedHereCount) {
      <small class="text-muted">
        {{ unresolvedHereCount }} element step(s) live on other pages — expected
        for cross-page tours.
      </small>
    }
    <button
      type="button"
      class="ms-auto k-button k-button-sm k-primary"
      [disabled]="invalidSteps.length > 0"
      (click)="onComplete()"
    >
      Complete
    </button>
  </div>
</form>
